import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { createPortal } from 'react-dom';
import { CustomPurchaseSlotContext } from '@modules/screen-view-v2/customPurchaseSlot';
import type { CustomPurchaseSlotInput, CustomPurchaseSlotMount } from '@modules/screen-view-v2/customPurchaseSlot';

type MountWindow = Window & { __segmentlyMountPurchaseButton?: CustomPurchaseSlotMount };
interface Mount { carrier: HTMLDivElement; input: CustomPurchaseSlotInput; dispose(): void; }

/** Same-origin DOM capability only. Payment creation and outcome delivery belong to
 * the existing host hook and MessageChannel. The authored button is never moved. */
export function useIframePurchaseSlots(screenId: string, frameRef: RefObject<HTMLIFrameElement | null>) {
  const renderer = useContext(CustomPurchaseSlotContext);
  const current = useRef({ screenId, available: !!renderer }); current.current = { screenId, available: !!renderer };
  const active = useRef<Mount | null>(null);
  const binding = useRef<{ doc: Document; screenId: string; mount: CustomPurchaseSlotMount } | null>(null);
  const [mounted, setMounted] = useState<Mount | null>(null);
  const clear = useCallback(() => { active.current?.dispose(); }, []);
  useEffect(() => clear, [screenId, !!renderer, clear]);
  useEffect(() => () => {
    const bound = binding.current;
    const win = bound?.doc.defaultView as MountWindow | null;
    if (win && win.__segmentlyMountPurchaseButton === bound?.mount) delete win.__segmentlyMountPurchaseButton;
    binding.current = null;
  }, []);

  const bind = useCallback(() => {
    const frame = frameRef.current;
    const doc = frame?.contentDocument;
    if (!frame?.isConnected || !doc?.defaultView) { clear(); return; }
    if (binding.current?.doc === doc && binding.current.screenId === screenId) return;
    clear();
    const mount: CustomPurchaseSlotMount = (target, input) => {
      if (!current.current.available || current.current.screenId !== screenId ||
        frameRef.current !== frame || frame.contentDocument !== doc || !frame.isConnected ||
        !target.isConnected || target.getRootNode() !== doc || target.tagName !== 'DIV' ||
        target.childNodes.length || active.current) return null;
      const lifetime = new AbortController();
      const carrier = doc.createElement('div'); carrier.style.width = '100%'; target.append(carrier);
      const entry: Mount = { carrier, input: { ...input, screenId, layoutTarget: target, frameDocument: doc, signal: lifetime.signal },
        dispose: () => {
          if (active.current !== entry) return;
          active.current = null; lifetime.abort(); observer.disconnect(); carrier.remove(); setMounted(null);
          try { input.onDispose?.(); } catch { /* Authored callback cannot interrupt host retirement. */ }
        } };
      const observer = new MutationObserver(() => {
        if (frameRef.current !== frame || !frame.isConnected || frame.contentDocument !== doc ||
          !target.isConnected || target.getRootNode() !== doc || carrier.parentNode !== target) entry.dispose();
      });
      observer.observe(doc.documentElement, { childList: true, subtree: true });
      observer.observe(frame.ownerDocument.documentElement, { childList: true, subtree: true });
      active.current = entry; setMounted(entry);
      return entry.dispose;
    };
    binding.current = { doc, screenId, mount };
    (doc.defaultView as MountWindow).__segmentlyMountPurchaseButton = mount;
  }, [frameRef, screenId, clear]);

  return { bind, clear, portal: renderer && mounted && mounted.input.screenId === screenId && !mounted.input.signal.aborted
    ? createPortal(renderer(mounted.input), mounted.carrier, mounted.input.requestId) : null };
}
